import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { SAVE_SUBREGION } from '../graphql/mutations';
import {
    draftReducer,
    draftView,
    isDraftDirty,
    needsReconcile,
    newDraftKey,
    planSubregionCreate,
    planSubregionSave,
    seedDraft,
} from './subregionDraft';
import { mutationResult } from './utils';

// Refetched, not cache-patched: it touches three tables. Awaited so the panel reads saved state
// as soon as Save resolves.
export const useSaveSubregion = ({ refetchQuery, onError }) => {
    const [saveSubregion] = useMutation(SAVE_SUBREGION, {
        onError,
        refetchQueries: () => [refetchQuery],
        awaitRefetchQueries: true,
    });
    return useCallback((variables) => saveSubregion({ variables }), [saveSubregion]);
};

// Never left on after the panel closes or the page unmounts.
export const useSyncPanelDirty = (setDirty, isDirty) => {
    useEffect(() => {
        setDirty(isDirty);
        return () => setDirty(false);
    }, [setDirty, isDirty]);
};

// Every edit stays local until Save. Reseeds when the subject changes and reconciles when saved
// state moves underneath. `save` returns whether it committed.
export const useSubregionDraft = ({ subregion, isNew, callbacks, onSaved }) => {
    const editing = isNew ? 'new' : subregion?.subregion_id || null;

    const [draft, dispatch] = useReducer(draftReducer, null, () => seedDraft(subregion, editing));
    const [saving, setSaving] = useState(false);

    // Adjust-state-on-prop-change, not an effect, so the map never paints the previous subregion's
    // zips. Settles in one render.
    if (draft.editing !== editing) {
        dispatch({ type: 'reseed', subregion, editing });
    } else if (needsReconcile(draft, subregion)) {
        dispatch({ type: 'reconcile', subregion });
    }

    const view = useMemo(() => draftView(subregion, draft), [subregion, draft]);
    // A ref, so the carrier actions stay stable while still passing the latest saved carriers.
    const savedCarriersRef = useRef(subregion?.carriers);
    savedCarriersRef.current = subregion?.carriers;
    const isDirty = useMemo(() => isDraftDirty(subregion, draft, isNew), [subregion, draft, isNew]);

    const actions = useMemo(
        () => ({
            setName: (name) => dispatch({ type: 'setName', name }),
            toggle: (zip) => dispatch({ type: 'toggleZip', zip }),
            addZipsToDraft: (zips) => dispatch({ type: 'addZips', zips }),
            removeZipFromDraft: (zip) => dispatch({ type: 'removeZip', zip }),
            addCarrier: (carrier) =>
                dispatch({ type: 'addCarrier', carrier, saved: savedCarriersRef.current, draftKey: newDraftKey() }),
            removeCarrier: (mapping) => dispatch({ type: 'removeCarrier', saved: savedCarriersRef.current, mapping }),
            setPrimary: (mapping) => dispatch({ type: 'setPrimary', saved: savedCarriersRef.current, mapping }),
            updateCarrier: (mapping) => dispatch({ type: 'updateCarrier', mapping }),
            setSetting: (key, value) => dispatch({ type: 'setSetting', key, value }),
        }),
        []
    );

    const save = useCallback(async () => {
        setSaving(true);
        try {
            if (isNew) {
                // One nested insert, not create-then-add.
                const data = mutationResult(await callbacks.createSubregion(planSubregionCreate(draft)));
                // Auto Assign's create returns `result`; Regions' returns `created`.
                const created = data?.result || data?.created;
                // No row: it failed (the mutation toasts). Reporting success would drop the draft.
                if (!created) return false;
                onSaved?.(created);
                return true;
            }

            const variables = planSubregionSave(subregion, draft);
            if (variables) {
                // All or nothing: a failure keeps the whole draft.
                const data = mutationResult(await callbacks.saveSubregion(variables));
                if (!data) return false;
                // update_subregions_by_pk returns null, not an error, for a missing or unwritable row.
                if (!data.subregion) {
                    callbacks.setNotification?.({
                        severity: 'error',
                        message: 'Subregion could not be saved. It may have been deleted -- refresh and try again.',
                    });
                    return false;
                }
                // The refetch has landed; reseed so saved edits aren't shown twice.
                dispatch({ type: 'invalidate' });
            }
            onSaved?.(subregion);
            return true;
        } finally {
            setSaving(false);
        }
    }, [isNew, draft, subregion, callbacks, onSaved]);

    return { ...view, ...actions, isDirty, saving, save };
};

// Everything that opens, closes or re-points the panel goes through `request`, so the
// unsaved-changes guard runs in one place. The dirty flag lives in a ref, so typing doesn't
// re-render the list.
export const usePanelSubject = ({ selectedSubregionId, callbacks }) => {
    const [creating, setCreating] = useState(false);
    const [pending, setPending] = useState(null);
    const isDirty = useRef(false);
    // A ref, so `commit` isn't re-created on every selection.
    const selectedId = useRef(selectedSubregionId);
    selectedId.current = selectedSubregionId;

    const commit = useCallback(
        (intent) => {
            switch (intent.kind) {
                case 'select':
                    // Clicking the open subregion closes the panel.
                    setCreating(false);
                    callbacks.toggleSubregion(intent.id);
                    break;
                case 'new':
                    if (selectedId.current) callbacks.toggleSubregion(selectedId.current);
                    setCreating(true);
                    break;
                case 'close':
                    if (selectedId.current) callbacks.toggleSubregion(selectedId.current);
                    setCreating(false);
                    break;
                case 'leave':
                    // Leaving the page; `proceed` does the navigating.
                    setCreating(false);
                    intent.proceed();
                    break;
                case 'saved':
                    setCreating(false);
                    // Select the new row, which unlocks carriers.
                    if (intent.id && intent.id !== selectedId.current) callbacks.toggleSubregion(intent.id);
                    break;
                default:
                    break;
            }
        },
        [callbacks]
    );

    const request = useCallback(
        (intent) => {
            // A save is the one transition that cannot be discarding anything.
            if (intent.kind !== 'saved' && isDirty.current) {
                setPending(intent);
                return;
            }
            commit(intent);
        },
        [commit]
    );

    const discardPrompt = useMemo(
        () => ({
            open: !!pending,
            onCancel: () => setPending(null),
            onDiscard: () => {
                setPending(null);
                if (pending) commit(pending);
            },
        }),
        [pending, commit]
    );

    return {
        creating,
        request,
        discardPrompt,
        setDirty: useCallback((next) => {
            isDirty.current = next;
        }, []),
    };
};
